import { CustomMetadataField } from "vl-api/vlSchemas";
import useQueryParam from "./useQueryParam";
import { ENTITY_TYPE_PARAMETER } from "../helpers/constants/miscellaneous";
import {
  FilterMenuItemType,
  Filters,
  isValidEntityType,
  QueryOperator,
} from "../types";
import { useDispatch, useSelector } from "react-redux";
import { getCaptions } from "../redux/SingleDataset/selectors";
import { useSearchOptions } from "./useFeatureFlagHooks";
import { useMislabelThresholdRange } from "./useFeatureFlagHooks";
import { useFilterConfig } from "./useFilterConfig";
import {
  convertAPIOperator,
  getFilterTitle,
} from "../helpers/utility/queryFilterHelpers";
import styles from "../views/components/FilterMenu/style.module.scss";
import {
  getCustomMetadataDisplayName,
  isCustomMetadata,
} from "../helpers/utility/customMetadataHelpers";
import { useCallback, useMemo } from "react";
import { App } from "antd";
import { useNavigate } from "react-router";
import { ModalFuncProps } from "antd/es/modal/interface";
import { clearFilters } from "../redux/SingleDataset/actions";
import {
  createAndUpdateIssueTypesFilter,
  isVqlQuery,
} from "../helpers/utility/vqlHelpers";
import useVqlParams from "./useVqlParams";

const getColumnClass = (filterType: FilterMenuItemType) => {
  const filterToColumnMap: Record<Filters, string> = {
    [Filters.FILE]: styles.column1,
    [Filters.FOLDER]: styles.column1,
    [Filters.LABELS]: styles.column1,
    [Filters.CAPTION]: styles.column1,
    [Filters.USER_TAGS]: styles.column1,

    [Filters.ISSUES]: styles.column2,
    [Filters.OUTLIERS]: styles.column2,
    [Filters.MISLABELS]: styles.column2,
    [Filters.UNIQUENESS]: styles.column2,
    [Filters.DUPLICATES]: styles.column2,
    [Filters.LABEL_OUTLIER]: styles.column2,
    [Filters.SIZE]: styles.column2,
    [Filters.CREATED_AT]: styles.column2,
    [Filters.MEDIA_STATUS]: styles.column2,
    [Filters.SEMANTIC]: "",
    [Filters.VISUAL_SIMILARITY]: "",
  };

  if (isCustomMetadata(filterType)) {
    return styles.column3;
  }
  return filterToColumnMap[filterType as Filters];
};

// Helper function to determine how many columns are actually needed
const getActiveColumns = (
  filterOptions: Array<{ type: FilterMenuItemType }>,
) => {
  const activeColumns = new Set<string>();

  filterOptions.forEach((option) => {
    const columnClass = getColumnClass(option.type);
    activeColumns.add(columnClass);
  });

  return Math.max(1, activeColumns.size);
};

const getDisabledTextByFilterType = (
  filterType: FilterMenuItemType,
): string => {
  const defaultDisabledText = "This filter is not available";

  if (isCustomMetadata(filterType)) {
    return "No values available for this field";
  }

  const disabledTextMap: Record<Filters, string> = {
    [Filters.CAPTION]: "No captions available for this dataset",
    [Filters.DUPLICATES]: "No duplicates available for this dataset",
    [Filters.FILE]: defaultDisabledText,
    [Filters.LABELS]: defaultDisabledText,
    [Filters.ISSUES]: defaultDisabledText,
    [Filters.FOLDER]: defaultDisabledText,
    [Filters.OUTLIERS]: defaultDisabledText,
    [Filters.USER_TAGS]: defaultDisabledText,
    [Filters.MISLABELS]: defaultDisabledText,
    [Filters.UNIQUENESS]: defaultDisabledText,
    [Filters.LABEL_OUTLIER]: defaultDisabledText,
    [Filters.SIZE]: defaultDisabledText,
    [Filters.CREATED_AT]: defaultDisabledText,
    [Filters.MEDIA_STATUS]: defaultDisabledText,
    [Filters.SEMANTIC]: defaultDisabledText,
    [Filters.VISUAL_SIMILARITY]: defaultDisabledText,
  };

  return disabledTextMap[filterType as Filters];
};

const defaultErrorModal: ModalFuncProps = {
  title: "This filter is not available",
  content: "This filter is not available for this dataset",
  okText: "OK",
};

const customMetadataErrorModal: ModalFuncProps = {
  title: "No values available for this field",
  content:
    "This field has no values available for this dataset, please add them to use this filter.",
  okText: "OK",
};

type UseFiltersMenu = {
  datasetId: string;
  onSelect: (type: FilterMenuItemType) => void;
  searchQuery?: string;
  customMetadataData?: CustomMetadataField[] | null;
};

export const useFiltersMenu = ({
  onSelect,
  datasetId,
  searchQuery,
  customMetadataData,
}: UseFiltersMenu) => {
  const {
    modal: { confirm, warning },
  } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { vqlQueries, addVqlQuery } = useVqlParams();
  const handleGotoEnrichment = useCallback(() => {
    dispatch(clearFilters() as any);
    navigate(`/dataset/${datasetId}/enrich`);
  }, [dispatch, navigate, datasetId]);
  const errorModalPropsByFilter = useMemo<Record<string, ModalFuncProps>>(
    () => ({
      [Filters.CAPTION]: {
        title: "No captions available for this dataset",
        content:
          "To use search by caption, enrich your dataset using one of our Image-to-Text models. This will generate captions and unlock the caption filter",
        okText: "Go to Enrichment",
        cancelText: "Close",
        onOk: () => handleGotoEnrichment(),
      },
      [Filters.DUPLICATES]: {
        title: "Duplicates filter isn't available for this dataset",
        content: "You'll find it in datasets created with newer versions.",
        okText: "OK",
      },
      [Filters.FILE]: defaultErrorModal,
      [Filters.LABELS]: defaultErrorModal,
      [Filters.ISSUES]: defaultErrorModal,
      [Filters.FOLDER]: defaultErrorModal,
      [Filters.OUTLIERS]: defaultErrorModal,
      [Filters.LABEL_OUTLIER]: defaultErrorModal,
      [Filters.USER_TAGS]: defaultErrorModal,
      [Filters.MISLABELS]: defaultErrorModal,
      [Filters.UNIQUENESS]: defaultErrorModal,
      [Filters.SIZE]: defaultErrorModal,
      [Filters.CREATED_AT]: defaultErrorModal,
      [Filters.MEDIA_STATUS]: defaultErrorModal,
    }),
    [handleGotoEnrichment],
  );
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam =
    entityParam === null
      ? "IMAGES"
      : isValidEntityType(entityParam)
        ? entityParam
        : null;

  const hasCaptionSearch = useSearchOptions(
    entityTypeFromQueryParam,
  ).captionsSearch;
  const hasCaptions = useSelector((state) => getCaptions(state))?.length > 0;
  const captionsExist = hasCaptions || hasCaptionSearch;

  const initialFilterOptions = useFilterConfig({
    captionsEnabled: captionsExist,
    entityType: entityTypeFromQueryParam,
    customMetadataData,
  });
  const mislabelsRange = useMislabelThresholdRange();

  const filterOptions = useMemo(() => {
    let filterOptions = initialFilterOptions;

    // Apply search filter if searchQuery is provided
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filterOptions = filterOptions.filter((option) => {
        const displayName = isCustomMetadata(option.type)
          ? getCustomMetadataDisplayName(option.type.field_name)
          : getFilterTitle(option.type, "filter");
        return displayName.toLowerCase().includes(query);
      });
    }

    return filterOptions.map((option) => {
      const disabledText = option.disabled
        ? getDisabledTextByFilterType(option.type)
        : "";
      const displayName = isCustomMetadata(option.type)
        ? getCustomMetadataDisplayName(option.type.field_name)
        : getFilterTitle(option.type, "filter");

      const handleSelect = () => {
        if (option.disabled) {
          const modalProps = isCustomMetadata(option.type)
            ? customMetadataErrorModal
            : errorModalPropsByFilter[option.type];
          modalProps.onOk ? confirm(modalProps) : warning(modalProps);
          return;
        }
        //if thresholds donot exist for mislabels, do not open modal
        if (option.type === Filters.MISLABELS && !mislabelsRange) {
          const isApplied = vqlQueries.some(
            (q) =>
              isVqlQuery(q, "issues") &&
              q.issues.value === "mislabels" &&
              convertAPIOperator(q.issues?.mode) === QueryOperator.IS,
          );

          if (isApplied) return;

          const vqlQuery = createAndUpdateIssueTypesFilter(
            "mislabels",
            QueryOperator.IS,
            [0, 1],
          );
          addVqlQuery(vqlQuery);
          return;
        }

        onSelect(option.type);
      };
      return {
        type: option.type,
        icon: option.icon,
        name: displayName,
        disabled: !!option.disabled,
        tooltipContent: disabledText,
        handleSelect,
        className: getColumnClass(option.type),
      };
    });
  }, [
    addVqlQuery,
    mislabelsRange,
    initialFilterOptions,
    onSelect,
    vqlQueries,
    confirm,
    warning,
    errorModalPropsByFilter,
    searchQuery,
  ]);

  // Calculate how many columns we actually need
  const activeColumnCount = useMemo(
    () => getActiveColumns(filterOptions),
    [filterOptions],
  );

  return {
    activeColumnCount,
    filterOptions,
  };
};
